import React from 'react';
import { CheckCircle2, Clock, AlertTriangle } from 'lucide-react';
import { Workstream, WorkstreamType } from '../../types/integration';
import { formatDate } from '../../utils/formatters';

interface WorkstreamStatusProps {
  workstreams: Workstream[];
  workstreamTypes: Array<{
    id: WorkstreamType;
    name: string;
    icon: React.ComponentType<any>;
  }>;
}

export default function WorkstreamStatus({ workstreams, workstreamTypes }: WorkstreamStatusProps) {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'blocked':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  return (
    <div className="space-y-6">
      {workstreamTypes.map(type => {
        const typeWorkstreams = workstreams.filter(w => w.type === type.id);
        const completedCount = typeWorkstreams.filter(w => w.status === 'completed').length;
        const progress = typeWorkstreams.length > 0 
          ? (completedCount / typeWorkstreams.length) * 100 
          : 0;

        return (
          <div key={type.id} className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <type.icon className="w-5 h-5 text-gray-400 mr-2" />
                <h4 className="text-sm font-medium text-gray-900">{type.name}</h4>
              </div>
              <span className="text-sm text-gray-500">
                {completedCount} of {typeWorkstreams.length} complete
              </span>
            </div>

            <div className="space-y-4">
              {typeWorkstreams.map(workstream => (
                <div key={workstream.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {getStatusIcon(workstream.status)}
                      <span className="ml-2 text-sm font-medium text-gray-900">
                        {workstream.name}
                      </span>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      workstream.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : workstream.status === 'blocked'
                        ? 'bg-red-100 text-red-800'
                        : workstream.status === 'in_progress'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {workstream.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">
                      Due: {formatDate(workstream.endDate)}
                    </span>
                    <span className="text-gray-500">
                      Owner: {workstream.owner}
                    </span>
                  </div>

                  <div className="relative pt-1">
                    <div className="flex mb-2 items-center justify-between">
                      <div>
                        <span className="text-xs font-semibold inline-block text-blue-600">
                          {workstream.progress}%
                        </span>
                      </div>
                    </div>
                    <div className="overflow-hidden h-2 text-xs flex rounded bg-blue-100">
                      <div
                        className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-600"
                        style={{ width: `${workstream.progress}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">
                  Overall Progress
                </span>
                <span className="text-sm text-gray-500">
                  {progress.toFixed(1)}%
                </span>
              </div>
              <div className="mt-2 overflow-hidden h-2 text-xs flex rounded bg-gray-200">
                <div
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-600"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}